import React, { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Maximize, Minimize, Clock, User, PackageSearch, AlertTriangle, CheckCircle2, ZoomIn, ZoomOut } from "lucide-react";
import api from "../../../service/api";
import { WarehouseTask } from "../../../types/warehouse";
import { useAuthStore } from "../../../store/authStore";

export const LiveMonitor = () => {
  const { user } = useAuthStore();
  const firmName = user?.firmName || user?.firm?.name || "OPERASYON";

  const [tasks, setTasks] = useState<WarehouseTask[]>([]);
  const [recentCompleted, setRecentCompleted] = useState<any[]>([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Font Büyütme/Küçültme State'i (Local Storage Destekli)
  const [zoomLevel, setZoomLevel] = useState<number>(() => {
    const saved = localStorage.getItem('liveMonitorZoom');
    return saved ? parseFloat(saved) : 1;
  });

  // Zoom değiştiğinde kaydet
  useEffect(() => {
    localStorage.setItem('liveMonitorZoom', zoomLevel.toString());
  }, [zoomLevel]);
  
  // Sadece Eksik ürün bildirilenleri gizleme/gösterme
  const [showDelayed, setShowDelayed] = useState(true);

  // Zamanlayıcı (Her saniye saat ve duration güncellensin diye)
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Polling (5 saniyede bir sessiz API çağrısı)
  useEffect(() => {
    const fetchActiveTasks = async () => {
      try {
        const res = await api.get("/warehouse-task/live-monitor/active-tasks", {
          headers: { "x-silent": "true" } // Global loader'ı engellemek için
        });

        if (res.data?.items) {
          setTasks(res.data.items);
          
          // Yeni tamamlananları yakalayıp kayan yazıya (marquee) ekle
          const newCompleted = res.data.items.filter((t: any) => t.status === "COMPLETED" || (t.isDepo && t.status === "PENDING_PACKAGING" && t.customProgress >= 100)); // Depo teslimlerde %100 de tamamlanmış sayılır
          
          if (newCompleted.length > 0) {
            setRecentCompleted(prev => {
              const merged = [...newCompleted, ...prev];
              const unique = Array.from(new Map(merged.map(item => [item.id, item])).values());
              return unique.slice(0, 15); // Son 15 tanesini tut
            });
          }
        }
      } catch (error) {
        console.error("LiveMonitor fetch error", error);
      }
    };

    fetchActiveTasks();
    const interval = setInterval(fetchActiveTasks, 5000);
    return () => clearInterval(interval);
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((err) => {
        console.error("Error attempting to enable fullscreen:", err.message);
      });
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  // Hesaplama ve Sıralama
  const processedTasks = useMemo(() => {
    return tasks.map((task) => {
      const isDepo = task.order?.shipment?.toUpperCase().includes("DEPO") || false;
      const accountName = task.order?.account?.name || "Bilinmiyor";
      const totalQty = task.items?.reduce((sum: number, item: any) => sum + (item.quantity || 0), 0) || 0;
      const collectedQty = task.items?.reduce((sum: number, item: any) => sum + (item.collectedQty || 0), 0) || 0;
      const packedQty = task.items?.reduce((sum: number, item: any) => sum + (item.packedQty || 0), 0) || 0;

      let customProgress = 0;
      if (totalQty > 0) {
        if (isDepo) {
          // Depo Teslim ise sadece toplama üzerinden hesapla
          customProgress = (collectedQty / totalQty) * 100;
        } else {
          // Normal kargo ise %50 toplama, %50 paketleme
          const pickP = collectedQty / totalQty;
          const packP = packedQty / totalQty;
          customProgress = (pickP * 50) + (packP * 50);
        }
      }

      // Elapsed Time (dk)
      const diffMs = currentTime.getTime() - new Date(task.createdAt).getTime();
      const elapsedMins = Math.floor(diffMs / 60000);

      // Sorumlu Kişi / Toplayan / Paketleyen
      const assignedName = task.assignedUser?.name || "Bekliyor";
      
      const pickingAction = task.actions?.find((a: any) => a.actionType === "PICKING_DONE");
      const packagingAction = task.actions?.find((a: any) => a.actionType === "PACKAGING_DONE") || task.actions?.find((a: any) => a.actionType === "PACKAGING_START");
      
      const pickerName = pickingAction?.user?.name || (customProgress > 0 ? assignedName : null);
      const packerName = packagingAction?.user?.name || (customProgress >= 100 ? assignedName : null);

      const pickingDoneMs = pickingAction?.createdAt ? currentTime.getTime() - new Date(pickingAction.createdAt).getTime() : 0;
      
      // Filtreleme: Depo teslimse ve 100% olduysa, üzerinden 5 dk geçmişse listeden gizle
      if (isDepo && customProgress >= 100 && pickingDoneMs > 5 * 60000) {
        return null;
      }
      
      // Sadece Eksikleri (SHORTAGE) gizleme filtresi
      if (!showDelayed && task.status === "SHORTAGE") {
        return null;
      }

      return {
        ...task,
        isDepo,
        accountName,
        customProgress: Math.min(customProgress, 100),
        elapsedMins,
        assignedName,
        pickerName,
        packerName
      };
    }).filter(Boolean).sort((a: any, b: any) => {
      // 0. Eksik olanlar her zaman EN ALTTA
      if (a.status === "SHORTAGE" && b.status !== "SHORTAGE") return 1;
      if (b.status === "SHORTAGE" && a.status !== "SHORTAGE") return -1;
      
      // 1. Depo Teslimler en üstte
      if (a.isDepo && !b.isDepo) return -1;
      if (!a.isDepo && b.isDepo) return 1;

      // 2. İlerleme yüzdesi büyük olanlar üstte
      if (b.customProgress !== a.customProgress) {
        return b.customProgress - a.customProgress;
      }

      // 3. Daha uzun süredir bekleyenler üstte
      return b.elapsedMins - a.elapsedMins;
    });
  }, [tasks, currentTime]);

  return (
    <div className={`flex flex-col bg-bg text-text font-sans ${isFullscreen ? 'fixed inset-0 z-[99999]' : 'h-[calc(100vh-80px)] rounded-2xl overflow-hidden shadow-2xl relative'}`}>

      {/* Header */}
      <div className="bg-card/80 backdrop-blur-md border-b border-borderColor px-6 py-4 flex justify-between items-center z-10">
        <div>
          <h1 className="text-2xl md:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-primary to-primaryHover tracking-tight uppercase">
            {firmName}
          </h1>
          <p className="text-text/70 text-sm mt-1 flex items-center gap-2 font-medium">
            <span className="w-2 h-2 rounded-full bg-success animate-pulse"></span>
            Canlı Operasyon İzleme
          </p>
        </div>

        <div className="flex items-center gap-4 md:gap-6">
          <div className="text-right hidden sm:block">
            <div className="text-2xl font-bold font-mono text-text">
              {currentTime.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </div>
            <div className="text-xs text-text/60 font-medium">
              {currentTime.toLocaleDateString('tr-TR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </div>
          </div>
          
          <div className="hidden sm:flex items-center bg-bgAlt border border-borderColor rounded-xl p-1 gap-1">
            <button
              onClick={() => setZoomLevel(z => Math.max(0.7, +(z - 0.1).toFixed(1)))}
              className="p-1.5 md:p-2 rounded-lg hover:bg-borderColor/50 text-text/70 hover:text-text transition-colors"
              title="Yazıları Küçült"
            >
              <ZoomOut size={18} />
            </button>
            <span className="text-xs font-bold font-mono text-text/50 min-w-[32px] text-center">
              {Math.round(zoomLevel * 100)}%
            </span>
            <button
              onClick={() => setZoomLevel(z => Math.min(2.0, +(z + 0.1).toFixed(1)))}
              className="p-1.5 md:p-2 rounded-lg hover:bg-borderColor/50 text-text/70 hover:text-text transition-colors"
              title="Yazıları Büyüt"
            >
              <ZoomIn size={18} />
            </button>
          </div>

          <button
            onClick={() => setShowDelayed(!showDelayed)}
            className={`px-3 py-2 md:px-4 md:py-2.5 rounded-xl font-bold text-xs md:text-sm transition-all border flex items-center gap-2 ${showDelayed ? 'bg-danger/10 text-danger border-danger/20 hover:bg-danger/20' : 'bg-surface-500/10 text-text/60 border-borderColor hover:bg-surface-500/20'}`}
            title="Eksik Ürünleri Gizle/Göster"
          >
            <AlertTriangle size={16} />
            <span className="hidden md:inline">
              {showDelayed ? "Eksikleri Gizle" : "Eksikleri Göster"}
            </span>
          </button>

          <button
            onClick={toggleFullscreen}
            className="p-3 rounded-xl bg-bgAlt hover:bg-borderColor/50 text-text transition-colors border border-borderColor"
            title={isFullscreen ? "Tam Ekrandan Çık" : "Tam Ekran Yap"}
          >
            {isFullscreen ? <Minimize size={24} /> : <Maximize size={24} />}
          </button>
        </div>
      </div>

      {/* List Container */}
      <div className="flex-1 overflow-y-auto p-4 bg-bgAlt/50 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-bgAlt/0 via-bgAlt/0 to-bgAlt pointer-events-none z-10 hidden" />
        
        <div style={{ zoom: zoomLevel }}>
          <ul className="flex flex-col gap-3 pb-20">
            <AnimatePresence>
              {processedTasks.length === 0 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex flex-col items-center justify-center h-40 text-text/50"
                >
                  <CheckCircle2 size={48} className="mb-4 text-text/40" />
                  <p className="text-lg font-medium">Aktif operasyon bulunmuyor.</p>
                </motion.div>
              )}

            {processedTasks.map((task) => {
              if (!task) return null;
              const p = task.customProgress;

              // Tema Uyumlu Renk ve Durum Çevirileri
              let baseColor = "primary";
              let statusText = task.status;

              switch (task.status) {
                case "PENDING_PICKING":
                  statusText = "Toplama Bekliyor";
                  baseColor = "text/40";
                  break;
                case "PICKING":
                  statusText = "Toplanıyor";
                  baseColor = "primary";
                  break;
                case "PICKING_PAUSED":
                  statusText = "Toplama Duraklatıldı";
                  baseColor = "warning";
                  break;
                case "PENDING_PACKAGING":
                  statusText = "Paketleme Bekliyor";
                  baseColor = "secondary";
                  break;
                case "PACKAGING":
                  statusText = "Paketleniyor";
                  baseColor = "secondary";
                  break;
                case "PACKAGING_PAUSED":
                  statusText = "Paketleme Duraklatıldı";
                  baseColor = "warning";
                  break;
                case "SHORTAGE":
                  statusText = "Eksik Ürün Bildirildi";
                  baseColor = "danger";
                  break;
                case "REVISION_NEEDED":
                  statusText = "Revizyon Bekliyor";
                  baseColor = "danger";
                  break;
                case "COMPLETED":
                  statusText = "Tamamlandı";
                  baseColor = "success";
                  break;
                default:
                  statusText = task.status;
                  baseColor = "text/40";
                  break;
              }

              if (p >= 100) {
                baseColor = "success";
                if (task.isDepo) {
                  statusText = "Teslime Hazır";
                } else if (task.status !== "COMPLETED") {
                  statusText = "Kargoya Hazır";
                }
              }

              if (task.isDepo && p < 100) {
                baseColor = "warning";
              }

              let pColor = "bg-blue-500";
              let accentClass = "border-l-blue-500";
              let cardBg = "bg-blue-500/5";
              let iconColor = "text-blue-500";

              if (baseColor === "text/40") {
                pColor = "bg-slate-400";
                accentClass = "border-l-slate-300";
                cardBg = "bg-slate-500/5";
                iconColor = "text-slate-400";
              } else if (baseColor === "primary") {
                pColor = "bg-blue-500";
                accentClass = "border-l-blue-500";
                cardBg = "bg-blue-500/5";
                iconColor = "text-blue-500";
              } else if (baseColor === "secondary") {
                pColor = "bg-purple-500";
                accentClass = "border-l-purple-500";
                cardBg = "bg-purple-500/5";
                iconColor = "text-purple-500";
              } else if (baseColor === "warning") {
                pColor = "bg-amber-500";
                accentClass = "border-l-amber-500";
                cardBg = "bg-amber-500/5";
                iconColor = "text-amber-500";
              } else if (baseColor === "danger") {
                pColor = "bg-red-500";
                accentClass = "border-l-red-500";
                cardBg = "bg-red-500/5";
                iconColor = "text-red-500";
              } else if (baseColor === "success") {
                pColor = "bg-emerald-500";
                accentClass = "border-l-emerald-500";
                cardBg = "bg-emerald-500/5";
                iconColor = "text-emerald-500";
              }

              // Süreyi okunabilir formata çevir
              const hours = Math.floor(task.elapsedMins / 60);
              const mins = task.elapsedMins % 60;
              const formattedTime = hours > 0 ? `${hours}s ${mins}d` : `${mins}dk`;

              // Kargo Firması Renklendirmesi
              const shipmentName = task.order?.shipment?.toUpperCase() || "KARGO";
              let shipmentBg = "bg-slate-500/10";
              let shipmentText = "text-text/80";
              let shipmentBorder = "border-borderColor";

              if (shipmentName.includes("ARAS")) {
                shipmentBg = "bg-blue-500/10"; shipmentText = "text-blue-500"; shipmentBorder = "border-blue-500/20";
              } else if (shipmentName.includes("YURT") || shipmentName.includes("YURTİÇİ")) {
                shipmentBg = "bg-emerald-500/10"; shipmentText = "text-emerald-500"; shipmentBorder = "border-emerald-500/20";
              } else if (shipmentName.includes("PTT")) {
                shipmentBg = "bg-yellow-500/10"; shipmentText = "text-yellow-600"; shipmentBorder = "border-yellow-500/20";
              } else if (shipmentName.includes("MNG")) {
                shipmentBg = "bg-orange-500/10"; shipmentText = "text-orange-500"; shipmentBorder = "border-orange-500/20";
              } else if (shipmentName.includes("SÜRAT") || shipmentName.includes("SURAT")) {
                shipmentBg = "bg-indigo-500/10"; shipmentText = "text-indigo-500"; shipmentBorder = "border-indigo-500/20";
              }

              return (
                <motion.li
                  key={task.id}
                  layout
                  initial={{ opacity: 0, y: 20, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.2 } }}
                  transition={{ type: "spring", stiffness: 300, damping: 25 }}
                  className={`grid grid-cols-12 gap-4 items-center px-4 py-4 rounded-xl border border-borderColor border-l-4 ${accentClass} ${cardBg} hover:opacity-80 transition-opacity shadow-sm`}
                >
                  {/* Status / Badge */}
                  <div className="col-span-1 flex justify-center">
                    {task.isDepo ? (
                      <span className="px-2 py-1 bg-warning/10 text-warning font-black text-[10px] rounded border border-warning/20 text-center uppercase">
                        DEPO<br />TESLİM
                      </span>
                    ) : task.status === "SHORTAGE" ? (
                      <span className="px-2 py-1 bg-danger/10 text-danger font-black text-[10px] rounded border border-danger/20 text-center uppercase flex flex-col items-center gap-0.5">
                        <AlertTriangle size={12} />
                        EKSİK
                      </span>
                    ) : (
                      <span className={`px-2 py-1 ${shipmentBg} ${shipmentText} font-bold text-[10px] rounded border ${shipmentBorder} text-center uppercase`}>
                        {shipmentName}
                      </span>
                    )}
                  </div>

                  {/* Customer */}
                  <div className="col-span-3 flex flex-col min-w-0">
                    <span className="text-sm font-black text-text truncate">
                      {task.accountName}
                    </span>
                    <span className="text-xs text-text/50 font-bold font-mono truncate mt-0.5">
                      Sipariş: {task.order?.orderNumber}
                    </span>
                  </div>

                  {/* Operation Status */}
                  <div className="col-span-2 flex items-center gap-2">
                    <PackageSearch size={14} className={iconColor} />
                    <span className="text-xs font-black uppercase tracking-wider text-text/80">
                      {statusText}
                    </span>
                  </div>

                  {/* Assigned User (Picker / Packer) */}
                  <div className="col-span-2 flex flex-col gap-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full bg-bg flex items-center justify-center border border-borderColor shrink-0">
                        <User size={10} className="text-text/60" />
                      </div>
                      <span className={`text-[11px] font-bold truncate ${task.pickerName ? 'text-primary' : 'text-text/40 italic'}`} title="Toplayan">
                        T: {task.pickerName || "Bekliyor"}
                      </span>
                    </div>
                    {!task.isDepo && (
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 rounded-full bg-bg flex items-center justify-center border border-borderColor shrink-0">
                          <PackageSearch size={10} className="text-text/60" />
                        </div>
                        <span className={`text-[11px] font-bold truncate ${task.packerName ? 'text-secondary' : 'text-text/40 italic'}`} title="Paketleyen">
                          P: {task.packerName || (p >= 100 ? task.pickerName : "Bekliyor")}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Elapsed Time */}
                  <div className="col-span-1 flex items-center justify-center gap-1.5">
                    <Clock size={12} className={task.elapsedMins > 60 ? "text-danger" : "text-text/40"} />
                    <span className={`text-xs font-black font-mono ${task.elapsedMins > 60 ? "text-danger" : "text-text/80"}`}>
                      {formattedTime}
                    </span>
                  </div>

                  {/* Progress Bar */}
                  <div className="col-span-3 flex items-center gap-3">
                    <div className="flex-1 h-2 bg-bg rounded-full overflow-hidden border border-borderColor relative">
                      <motion.div
                        className={`h-full ${pColor}`}
                        initial={{ width: 0 }}
                        animate={{ width: `${p}%` }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                      />
                    </div>
                    <span className="w-10 text-right text-sm font-black font-mono text-text shrink-0">
                      %{Math.round(p)}
                    </span>
                  </div>
                </motion.li>
              );
            })}
          </AnimatePresence>
        </ul>
        </div>
      </div>

      {/* Marquee Footer (Son Tamamlananlar) */}
      {recentCompleted.length > 0 && (
        <div className="bg-success text-white py-3 px-4 flex items-center overflow-hidden shrink-0 border-t border-success/30 relative z-20">
          <div className="font-black text-xs md:text-sm shrink-0 mr-4 whitespace-nowrap z-10 uppercase tracking-widest flex items-center gap-2">
            <CheckCircle2 size={16} />
            SON TAMAMLANANLAR:
          </div>
          <div className="flex-1 overflow-hidden relative" style={{ display: 'flex', whiteSpace: 'nowrap' }}>
            <motion.div
              className="flex items-center gap-12"
              animate={{ x: ["0%", "-100%"] }}
              transition={{ ease: "linear", duration: Math.max(recentCompleted.length * 6, 20), repeat: Infinity }}
              style={{ display: 'flex', whiteSpace: 'nowrap', width: 'fit-content' }}
            >
              {recentCompleted.map(task => {
                const pAction = task.actions?.find((a: any) => a.actionType === "PICKING_DONE");
                const pickerName = pAction?.user?.name || task.assignedUser?.name || "Bilinmiyor";
                return (
                  <span key={task.id} className="font-semibold text-sm flex items-center gap-1.5 opacity-90">
                    <PackageSearch size={14} className="opacity-60" />
                    {task.order?.account?.name || "Bilinmeyen Müşteri"} 
                    <span className="opacity-60 ml-1 font-mono text-xs">(Hazırlayan: {pickerName})</span>
                  </span>
                );
              })}
            </motion.div>
          </div>
        </div>
      )}
    </div>
  );
};
